import { num } from './format.js';

/* The Offers tab's words (docs/23 §3.3–3.4). Pure, so they're tested. */

/** What a game pays, in words: from the server's numbers (Admin → Offers → Games); never a promise the server hasn't made. */
export function gameRewardLine(reward: { points: number; perDay: number }): string {
  if (reward.points <= 0) return 'Points for every finished game.';
  const times = reward.perDay <= 1 ? 'once a day' : `up to ${num(reward.perDay)} times a day`;
  return `${num(reward.points)} points for every finished game, ${times}.`;
}

/* ---------- The Rewards and Earn sheets on Home (Saif, 21 Oct 2026 evening): what they show, worked out from the data ---------- */

export interface RewardTierView { id: 'side' | 'main'; name: string; points: number; ready: boolean; toGo: number }

/** The next free item and how far it is: a side first, then a main; null once both are affordable. */
export function nextReward(freePoints: number, rp: { side: number; main: number }): { left: number; what: 'a free side' | 'a free main'; pct: number } | null {
  if (freePoints < rp.side) return { left: rp.side - freePoints, what: 'a free side', pct: Math.min(100, Math.round((freePoints / rp.side) * 100)) };
  if (freePoints < rp.main) return { left: rp.main - freePoints, what: 'a free main', pct: Math.min(100, Math.round((freePoints / rp.main) * 100)) };
  return null;
}

/** Each reward tier with whether it can be claimed now and how many points are still to go. */
export function rewardTiers(freePoints: number, tiers: readonly { id: 'side' | 'main'; name: string; points: number }[]): RewardTierView[] {
  return tiers.map((t) => ({ id: t.id, name: t.name, points: t.points, ready: freePoints >= t.points, toGo: Math.max(0, t.points - freePoints) }));
}

export type EarnRowId = 'invite' | 'spin' | 'email' | 'games' | 'receipt';

/**
 * The Earn sheet's rows, in order, from what is on: Invite a friend (always), Spin the wheel (only while an order has a
 * spin waiting), Add your email (while the bonus is still to come), Play and earn (games on, with at least one game),
 * Points from a receipt (always).
 */
export function earnRows(o: { spinWaiting: boolean; emailBonusPoints: number; gamesOn: boolean; gamesCount: number }): EarnRowId[] {
  const rows: EarnRowId[] = ['invite'];
  if (o.spinWaiting) rows.push('spin');
  if (o.emailBonusPoints > 0) rows.push('email');
  if (o.gamesOn && o.gamesCount > 0) rows.push('games');
  rows.push('receipt');
  return rows;
}
